import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUp, Sparkles } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import PageShell from "@/components/PageShell";
import MessageBubble from "@/components/MessageBubble";
import ReflectionCard from "@/components/ReflectionCard";
import RotatingHint from "@/components/RotatingHint";
import KrishnaAvatar from "@/components/KrishnaAvatar";
import { parseKrishnaAnswer, reflectionTakeaway } from "@/lib/parseAnswer";
import { pushRecentMessage, loadRecentMessages } from "@/lib/storage";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function KrishnaAvatarPresence() {
  return <KrishnaAvatar size={40} />;
}

const GREETING = {
  id: "greeting",
  role: "ai",
  text: "Hi, I am KALKI, your friend.\n\nI am here with you.\n\nHow has your day been? What shall we explore together today?",
  isGreeting: true,
};

const HINTS = [
  "KALKI, what should I do today?",
  "Guide me about my purpose.",
  "How can I stay peaceful?",
  "Teach me courage.",
  "Help me understand love.",
  "Inspire me today.",
];

export default function Chat() {
  const navigate = useNavigate();
  const location = useLocation();
  const [messages, setMessages] = useState(() => {
    const recent = loadRecentMessages();
    if (recent && recent.length > 0) return recent;
    return [GREETING];
  });
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [reflection, setReflection] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const didSeedRef = useRef(false);

  // Seed from Landing chip
  useEffect(() => {
    if (didSeedRef.current) return;
    didSeedRef.current = true;
    const seed = location.state?.seed;
    if (seed) {
      handleSend(seed);
      navigate(location.pathname, { replace: true, state: {} });
    } else {
      setTimeout(() => inputRef.current?.focus(), 200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cmd/Ctrl + K to focus input
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Autoscroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [messages, loading]);

  const handleSend = async (overrideText) => {
    const q = (overrideText ?? input).trim();
    if (!q || loading) return;

    setInput("");
    const userMsg = { id: crypto.randomUUID(), role: "user", text: q };
    setMessages((m) => {
      const next = [...m, userMsg];
      pushRecentMessage(next);
      return next;
    });
    setLoading(true);

    try {
      const res = await axios.post(`${API}/ask`, { question: q }, { timeout: 60000 });
      const answer = res.data?.answer || "";
      const parsed = parseKrishnaAnswer(answer);
      const aiMsg = {
        id: crypto.randomUUID(),
        role: "ai",
        text: answer,
        parsed,
      };
      setMessages((m) => {
        const next = [...m, aiMsg];
        pushRecentMessage(next);
        return next;
      });
      setReflection({
        question: q,
        takeaway: reflectionTakeaway(parsed),
        parsed,
        createdAt: new Date().toISOString(),
      });
    } catch (err) {
      console.error(err);
      toast.error("KALKI is quiet for a moment. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <PageShell>
      {/* Header */}
      <header
        data-testid="chat-header"
        className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-neutral-100"
      >
        <div className="mx-auto w-full max-w-6xl px-5 sm:px-8 py-3.5 flex items-center gap-3">
          <button
            data-testid="back-btn"
            onClick={() => navigate("/")}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-neutral-100 text-neutral-500 hover:text-neutral-900 hover:border-neutral-200 transition-colors"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-display text-[18px] sm:text-[20px] font-light tracking-[-0.02em] text-neutral-950 leading-none">
              KALKI
            </div>
            <div className="text-[10.5px] sm:text-[11px] tracking-wide text-neutral-400 mt-1">
              A conversation with KALKI
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 mr-2 text-[10.5px] tracking-wider uppercase text-neutral-400">
            <kbd className="px-1.5 py-0.5 rounded-md border border-neutral-200 bg-white/70 font-medium text-neutral-500">⌘K</kbd>
            <span>focus</span>
          </div>
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-b from-[#FFF7E4] to-white border border-[#EFDDB2]">
            <Sparkles className="w-3.5 h-3.5 text-[#8C6B29]" strokeWidth={1.5} />
          </div>
        </div>
      </header>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="kalki-scroll flex-1 overflow-y-auto"
      >
        <div className="mx-auto w-full max-w-3xl px-5 sm:px-8 pt-8 pb-48">
          <div className="flex flex-col gap-6">
            <AnimatePresence initial={false}>
              {messages.map((m) => (
                <MessageBubble
                  key={m.id}
                  role={m.role}
                  text={m.text}
                  parsed={m.parsed}
                  isGreeting={m.isGreeting}
                />
              ))}
            </AnimatePresence>

            {loading && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3 w-full"
                data-testid="typing-indicator"
              >
                <div className="mt-0.5 relative">
                  {/* Krishna avatar with soft feather glow while typing */}
                  <div className="feather-glow-ring absolute inset-0 rounded-full" aria-hidden />
                  <KrishnaAvatarPresence />
                </div>
                <div
                  className="relative bg-white/85 backdrop-blur-md rounded-3xl rounded-tl-sm px-5 py-4 border shadow-[0_10px_30px_-20px_rgba(197,160,89,0.5)] flex items-center gap-3"
                  style={{ borderColor: "rgba(197,160,89,0.35)" }}
                >
                  <div className="flex items-center">
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                    <span className="typing-dot" />
                  </div>
                  <span className="text-[11.5px] tracking-wide text-neutral-500 italic">
                    KALKI is listening&hellip;
                  </span>
                  <span className="typing-feather" aria-hidden>
                    <svg viewBox="0 0 24 24" width="14" height="14">
                      <defs>
                        <linearGradient id="tf-g" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#0FA3A3" />
                          <stop offset="100%" stopColor="#C5A059" />
                        </linearGradient>
                      </defs>
                      <path d="M12 2 C 6 6, 6 18, 10 22 C 15 20, 17 10, 12 2 Z" fill="url(#tf-g)" />
                      <ellipse cx="12" cy="9" rx="1.2" ry="2" fill="#C5A059" />
                    </svg>
                  </span>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Input bar */}
      <div className="absolute bottom-0 left-0 right-0 z-40 pb-6 pt-6 bg-gradient-to-t from-white via-white/95 to-white/0 pointer-events-none">
        <div className="pointer-events-auto mx-auto w-full max-w-[min(1200px,92vw)] min-w-0 px-5 sm:px-6">
          <RotatingHint
            hints={HINTS}
            onPick={(h) => {
              setInput(h);
              inputRef.current?.focus();
            }}
          />
          <div className="relative mt-2 flex items-center gap-2 rounded-full border border-neutral-200 bg-white/90 backdrop-blur px-4 py-2 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)] input-focus-glow">
            <input
              ref={inputRef}
              data-testid="ask-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="What would you like to ask me today?"
              className="flex-1 bg-transparent text-[14.5px] text-neutral-900 placeholder:text-neutral-400 px-3 py-2.5"
              disabled={loading}
            />
            <button
              data-testid="send-btn"
              onClick={() => handleSend()}
              disabled={loading || !input.trim()}
              className="liquid-gold flex h-10 w-10 items-center justify-center rounded-full bg-neutral-950 text-white transition-all hover:bg-neutral-800 disabled:opacity-30 disabled:cursor-not-allowed active:scale-95 relative overflow-hidden"
              aria-label="Send"
            >
              <ArrowUp className="w-4 h-4 relative z-10" />
            </button>
          </div>
          <p className="mt-2 text-center text-[10px] tracking-wider uppercase text-neutral-300">
            Spoken by KALKI · Not a replacement for professional help
          </p>
        </div>
      </div>

      <AnimatePresence>
        {reflection && (
          <ReflectionCard
            reflection={reflection}
            onClose={() => setReflection(null)}
            onContinue={() => {
              setReflection(null);
              setTimeout(() => inputRef.current?.focus(), 200);
            }}
          />
        )}
      </AnimatePresence>
    </PageShell>
  );
}
